<template>
	<article :class="['wallet-coupon-card', themeClass, statusClass]" tabindex="0">
		<div class="wallet-coupon-card__body">
			<div class="wallet-coupon-card__details">
				<div class="wallet-coupon-card__title-row">
					<!-- 券名称 -->
					<h3 :title="coupon.couponName">{{ coupon.couponName }}</h3>
					<!-- 券类型 -->
					<span class="wallet-coupon-card__type">
						<i class="iconfont icon-biaoqian" aria-hidden="true"></i>
						{{ coupon.couponTypeName }}
					</span>
				</div>
				<!-- 适用范围、使用门槛、最高优惠 -->
				<p>{{ coupon.applicableScopeName }}</p>
				<p>{{ thresholdText }}</p>
				<p>{{ discountCapText }}</p>
			</div>

			<div class="wallet-coupon-card__value">
				<!-- 面值 -->
				<div>
					<span v-if="valueUnit === '元'" class="currency">¥</span>
					<strong>{{ coupon.faceValue }}</strong>
					<span v-if="valueUnit !== '元'">{{ valueUnit }}</span>
				</div>
				<!-- 叠加策略 -->
				<p :title="stackStrategyText">{{ stackStrategyText }}</p>
			</div>
		</div>

		<footer class="wallet-coupon-card__footer">
			<!-- 有效期 -->
			<span>{{ validityText }}</span>
			<button type="button" :disabled="coupon.couponStatus !== 'unused'" @click="handleUse">{{ buttonText }}</button>
		</footer>
	</article>
</template>

<script>
	import { parseTime } from '@/utils'

	export default {
		name: 'WalletCouponCard',
		props: {
			coupon: {
				type: Object,
				required: true
			}
		},
		computed: {
			// couponType 字典：10 满减券、20 折扣券、30 体验券
			themeClass() {
				return { 10: 'is-full-reduction', 20: 'is-discount', 30: 'is-trial' }[Number(this.coupon.couponType)] || ''
			},
			// couponStatus 字典：unused 未使用、used 已使用、expired 已过期
			statusClass() {
				return `is-${this.coupon.couponStatus}`
			},
			valueUnit() {
				const couponType = Number(this.coupon.couponType)
				if (couponType === 10) return '元'
				if (couponType === 20) return '折'
				if (couponType === 30) return Number(this.coupon.measureType) === 20 ? '天' : '次'
				return ''
			},
			thresholdText() {
				const thresholdAmount = Number(this.coupon.thresholdAmount) || 0
				return thresholdAmount > 0 ? `满${thresholdAmount}元可用` : '无门槛'
			},
			discountCapText() {
				const discountCapAmount = Number(this.coupon.discountCapAmount) || 0
				return discountCapAmount > 0 ? `${discountCapAmount}元` : '无最高优惠限制'
			},
			stackStrategyText() {
				return { 10: '不可叠加', 20: '可叠加', 30: '互斥使用' }[Number(this.coupon.stackStrategy)] || '叠加规则以券面为准'
			},
			validityText() {
				const startTime = this.formatCouponDate(this.coupon.issueStartTime)
				const endTime = this.formatCouponDate(this.coupon.invalidTime || this.coupon.useEndTime)
				if (!endTime) return '长期有效'
				if (!startTime) return `有效期至 ${endTime}`
				return `${startTime} 至 ${endTime}`
			},
			buttonText() {
				return { unused: '去使用', used: '已使用', expired: '已过期' }[this.coupon.couponStatus] || '去使用'
			}
		},
		methods: {
			formatCouponDate(value) {
				if (value === undefined || value === null || value === '') return ''
				let time = value
				if (typeof time === 'string' && /^\d+$/.test(time.trim())) {
					time = Number(time)
				}
				if (typeof time === 'number' && Number.isFinite(time) && String(Math.trunc(Math.abs(time))).length === 10) {
					time *= 1000
				}
				return parseTime(time, '{y}-{m}-{d}') || String(value).slice(0, 10)
			},
			handleUse() {
				if (this.coupon.couponStatus === 'unused') this.$emit('use', this.coupon)
			}
		}
	}
</script>

<style lang="scss" scoped>
	.wallet-coupon-card {
		--theme: #df4518;
		--field: #f96a21;
		--tag: #fd663d;
		--divider: #fdb955;
		--button-start: #ffc578;
		--button-end: #fa6c21;
		--button-shadow: rgba(250, 108, 33, 0.34);
		box-sizing: border-box;
		// min-height: 188px;
		padding: 18px 15px 14px 20px;
		outline: none;
		color: #666;
		background: url('~@/assets/coupon/full-reduction-coupon-bg.png') center / 100% 100% no-repeat;
		transition: filter 0.22s ease, transform 0.22s ease;
	}
	.wallet-coupon-card:hover,
	.wallet-coupon-card:focus-visible {
		transform: translateY(-4px);
		filter: drop-shadow(0 8px 10px rgba(55, 111, 170, 0.13));
	}
	.wallet-coupon-card.is-trial {
		--theme: #3072f9;
		--field: #4a8bf9;
		--tag: #4297fa;
		--divider: #d0e6fd;
		--button-start: #6fb5ff;
		--button-end: #0c6ef5;
		--button-shadow: rgba(12, 110, 245, 0.3);
		background-image: url('~@/assets/coupon/trial-coupon-bg.png');
	}
	.wallet-coupon-card.is-discount {
		--theme: #7045f1;
		--field: #997af6;
		--tag: #8b5ef5;
		--divider: #d7c5fb;
		--button-start: #ceb8ff;
		--button-end: #7143ef;
		--button-shadow: rgba(113, 67, 239, 0.3);
		background-image: url('~@/assets/coupon/discount-coupon-bg.png');
	}
	.wallet-coupon-card.is-used,
	.wallet-coupon-card.is-expired {
		--theme: #666;
		--field: #777;
		--tag: #777;
		--divider: #d2d2d2;
		--button-start: #d4d4d4;
		--button-end: #777;
		--button-shadow: rgba(102, 102, 102, 0.18);
		background-image: url('~@/assets/coupon/coupon-disabled-bg.png');
		filter: grayscale(1);
	}
	.wallet-coupon-card.is-used:hover,
	.wallet-coupon-card.is-used:focus-visible,
	.wallet-coupon-card.is-expired:hover,
	.wallet-coupon-card.is-expired:focus-visible {
		filter: grayscale(1) drop-shadow(0 7px 9px rgba(102, 102, 102, 0.12));
	}
	.wallet-coupon-card__body {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 100px;
		column-gap: 8px;
		min-height: 100px;
	}
	.wallet-coupon-card__details {
		min-width: 0;
		border-right: 1px dashed var(--divider);
		padding-right: 10px;
	}
	.wallet-coupon-card__title-row {
		display: flex;
		min-width: 0;
		align-items: center;
		gap: 8px;
		margin-bottom: 7px;
	}
	.wallet-coupon-card h3 {
		flex: 1;
		min-width: 0;
		margin: 0 auto 0 0;
		overflow: hidden;
		color: var(--theme);
		font-size: 17px;
		line-height: 23px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.wallet-coupon-card__type {
		display: inline-flex;
		height: 23px;
		padding: 0 8px;
		align-items: center;
		flex: 0 0 auto;
		border-radius: 12px;
		color: #fff;
		background: var(--tag);
		font-size: 12px;
		white-space: nowrap;
	}
	.wallet-coupon-card__type i {
		margin-right: 4px;
		font-size: 12px;
	}
	.wallet-coupon-card__details > p {
		margin: 3px 0 0;
		overflow: hidden;
		color: var(--field);
		font-size: 12px;
		line-height: 18px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.wallet-coupon-card__value {
		padding-top: 17px;
		text-align: center;
	}
	.wallet-coupon-card__value > div {
		height: 42px;
		color: var(--theme);
		line-height: 42px;
		white-space: nowrap;
	}
	.wallet-coupon-card__value strong {
		font-size: 34px;
	}
	.wallet-coupon-card__value span {
		font-size: 16px;
		font-weight: 700;
	}
	.wallet-coupon-card__value .currency {
		font-size: 17px;
	}
	.wallet-coupon-card__value p {
		display: -webkit-box;
		margin: 4px 0 0;
		overflow: hidden;
		color: #666;
		font-size: 12px;
		line-height: 18px;
		word-break: break-all;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
	}
	.wallet-coupon-card__footer {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 8px;
	}
	.wallet-coupon-card__footer span {
		min-width: 0;
		overflow: hidden;
		color: #666;
		font-size: 12px;
		line-height: 34px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.wallet-coupon-card__footer button {
		width: 96px;
		height: 34px;
		border: 0;
		border-radius: 17px;
		outline: none;
		flex: 0 0 96px;
		color: #fff;
		background: linear-gradient(180deg, var(--button-start), var(--button-end));
		box-shadow: 0 5px 10px var(--button-shadow);
		font-size: 14px;
		cursor: pointer;
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}
	.wallet-coupon-card__footer button:hover:not(:disabled),
	.wallet-coupon-card__footer button:focus-visible:not(:disabled) {
		box-shadow: 0 7px 13px var(--button-shadow);
		transform: translateY(-1px);
	}
	.wallet-coupon-card__footer button:disabled {
		cursor: not-allowed;
	}

	@media (prefers-reduced-motion: reduce) {
		.wallet-coupon-card,
		.wallet-coupon-card__footer button {
			transition: none;
		}
	}
</style>
